import { TitleAndValue } from '@components/UI-Elements/TitlesAndTexts/TitleAndValueList';
import React, { useEffect, useState } from 'react';
import { useMaterialsStore } from '../../../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../../../components/Stores/accountStore';
import { DateValueInputWithDateSwitch } from '../../../../components/UI-Elements/Charts/DateValueInputMultiChart/DateValueInputWithDateSwitch';
import { LoadingSpinnerOverlay } from '../../../../components/UI-Elements/LoadingSpinnerOverlay';
import { ViewDocumentOverlay } from '../../../../components/UI-Elements/ViewDocumentOverlay';
import { getFileByUrl } from '../../../../sp-firebase/storageFB';
import { Product } from '../../../../sp-types/productType';
import { getHighestAbsoluteValue } from '../../../../utils/MonthYear/MonthYearValue';
import {
  getStartingDate,
  mapMaterialToCharts,
  multiplyQuantityBy,
} from '../FOIndexedClientExposure/FillOrderAdjustmentsUtils';
import { MaterialBreakdownSummary } from '../MaterialBreakdownSummery/MaterialBreakdownSummery';
import { FOIndexedButtonSection } from './components/CreateOrderSummaryButtons';
import { FOSummaryInputs } from './components/FOSummaryInputs';
import {
  updateEditedFutureOrderCharts,
  getFutureOrderWithUpdateAdjustments,
  isSupplierAdjustmentMultiValue,
  isSupplierLeadTimeMultiValue,
} from './OrderSummaryUtils';
import { saveExistingFutureOrder } from '../../../../sp-firebase/futureOrderFB';
import { IFutureOrder } from '../../../../sp-types/FutureOrderType';
import { getLastDate } from '../AddNewFutureOrderUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

interface Props {
  products: Product[];
  onModifyOrder: () => void;
  routeToBackPage: () => void;
  editedFutureOrder: IFutureOrder;
  setEditedFutureOrder: (_value: IFutureOrder) => void;
}

export const FOIndexedSummary: React.FC<Props> = ({
  onModifyOrder,
  products,
  routeToBackPage,
  editedFutureOrder,
  setEditedFutureOrder,
}) => {
  const {
    orderId,
    clientName,
    fileUrl,
    orderDetails: { quantity, pricePerUnit, productDeliveryCalendar: productCalendar },
    clientExposure,
    supplierExposure,
    totalExposure,
    status,
  } = editedFutureOrder;
  const {
    account: { AdjustmentsInfo, materialsToAccount },
    materialColorDictionary: colorsMap,
  } = useAccountStore();
  const { materialsMap } = useMaterialsStore();
  const { pricesMap } = useCurrencyRates();
  const [showOverlay, setShowOverlay] = useState(false);
  const [fileDBUrl, setFileDBUrl] = useState('');

  // useEffect(() => {
  //   if (editedFutureOrder?.productId) {
  //     updateEditedFutureOrderCharts(editedFutureOrder, setEditedFutureOrder, products, materialsMap);
  //   }
  // }, [editedFutureOrder?.productId]);

  useEffect(() => {
    const fetchDocument = async () => {
      if (fileUrl.length > 0) {
        const docUrl = await getFileByUrl(fileUrl[0]);
        if (typeof docUrl === 'string') {
          setFileDBUrl(docUrl);
        }
      }
    };
    fetchDocument();
  }, [fileUrl]);

  const saveFutureOrder = async (updatedFutureOrder: IFutureOrder) => {
    await saveExistingFutureOrder(updatedFutureOrder);
  };
  const applyAdjustments = (paymentAdj?: string, supplierAdj?: string, leadTime?: number) => {
    const updatedFutureOrder = getFutureOrderWithUpdateAdjustments(
      editedFutureOrder,
      paymentAdj,
      supplierAdj,
      leadTime,
    );
    const updated = updateEditedFutureOrderCharts(updatedFutureOrder, products, materialsMap);
    setEditedFutureOrder(updated);

    saveFutureOrder(updated);
  };

  return editedFutureOrder?.orderDetails && clientExposure && editedFutureOrder.totalExposure ? (
    <div className="flex flex-col flex-1 pt-2 xl:pt-4">
      <div className=" flex flex-col flex-1 pt-2">
        <div className="flex flex-row gap-16">
          <TitleAndValue
            titleValueData={[
              { title: 'Order ID', valueAsString: orderId },
              ...(clientName ? [{ title: 'Client name', valueAsString: clientName }] : []),
            ]}
          />
        </div>
        <div className="flex flex-col flex-[0.25] py-1 /flexiblePlaceholder/"></div>
        <div className="flex">
          {supplierExposure.length !== 0 && Object.keys(clientExposure).length !== 0 && (
            <FOSummaryInputs
              editedOrder={editedFutureOrder}
              adjustmentOptions={AdjustmentsInfo.productAdjustmentOptions}
              saveValues={applyAdjustments}
              isSupplyAdjustmentMultiValue={isSupplierAdjustmentMultiValue(supplierExposure)}
              isSupplyLeadTimeMultiValue={isSupplierLeadTimeMultiValue(supplierExposure)}
              isDisabled={true}
            />
          )}
        </div>
        <div className="flex flex-col flex-[0.25] py-1"></div>

        <div className="max-h-40">
          <MaterialBreakdownSummary
            materialTotalExposure={totalExposure}
            materialsToAccount={materialsToAccount}
            totalQuantity={quantity}
            materialExposureData={supplierExposure}
            materialColorDictionary={colorsMap}
            materialsMap={materialsMap}
          />
        </div>

        <div className="flex flex-col flex-1"></div>

        <div>
          {productCalendar && (
            <DateValueInputWithDateSwitch
              mainChart={{
                data: multiplyQuantityBy(productCalendar || [], -(pricePerUnit || 1)),
                title: 'Product price',
                type: 'bar',
                transparent: true,
              }}
              isHideValueInput
              numberOfMonthsToShow={24}
              changeMonths={12}
              additionalCharts={mapMaterialToCharts(
                totalExposure?.map(({ materialId, exposureCalendar }) => ({
                  materialId,
                  exposureCalendar: exposureCalendar.map((dateValue) => ({
                    ...dateValue,
                    value: dateValue.value * pricesMap[materialId],
                  })),
                })) || ([] as any),
                materialsMap,
                colorsMap,
              )}
              startingDate={getStartingDate()}
              endDate={productCalendar && getLastDate(productCalendar)}
              suggestedMaxValue={productCalendar && getHighestAbsoluteValue(productCalendar) * 1.2}
              suggestedMinValue={productCalendar && -getHighestAbsoluteValue(productCalendar) * 1.2}
            />
          )}
        </div>
      </div>

      <FOIndexedButtonSection
        isEditable={status !== 'completed'}
        fileDBUrl={fileDBUrl}
        onModifyOrder={onModifyOrder}
        setShowOverlay={setShowOverlay}
        routeToBackPage={routeToBackPage}
      />

      {showOverlay && fileDBUrl && (
        <ViewDocumentOverlay fileUrl={fileDBUrl} uploadedDocumentPath={fileUrl[0]} setShowOverlay={setShowOverlay} />
      )}
    </div>
  ) : (
    <LoadingSpinnerOverlay />
  );
};
